import * as React from "react";
import de from "../i18n/de.json";
import en from "../i18n/en.json";
import es from "../i18n/es.json";
import fr from "../i18n/fr.json";
import { Locale, LocalizedText } from "../types";

const dictionaries = {
  en,
  de,
  es,
  fr
} as const;

type Messages = typeof en;

interface I18nContextValue {
  locale: Locale;
  setLocale: (locale: Locale) => void;
  messages: Messages;
  t: (path: string, variables?: Record<string, string | number>) => string;
  localizeText: (text: LocalizedText) => string;
}

const I18nContext = React.createContext<I18nContextValue | null>(null);
const STORAGE_KEY = "ainavify-locale";

function interpolate(template: string, variables?: Record<string, string | number>) {
  if (!variables) {
    return template;
  }

  return Object.entries(variables).reduce((result, [key, value]) => {
    return result.replace(new RegExp(`\\{${key}\\}`, "g"), String(value));
  }, template);
}

function resolvePath(messages: Messages, path: string): unknown {
  return path.split(".").reduce<unknown>((current, key) => {
    if (current && typeof current === "object" && key in (current as Record<string, unknown>)) {
      return (current as Record<string, unknown>)[key];
    }

    return undefined;
  }, messages);
}

function getBrowserLocale(): Locale {
  if (typeof window === "undefined") {
    return "en";
  }

  const stored = window.localStorage.getItem(STORAGE_KEY) as Locale | null;
  if (stored && stored in dictionaries) {
    return stored;
  }

  const browserLocale = window.navigator.language.slice(0, 2) as Locale;
  return browserLocale in dictionaries ? browserLocale : "en";
}

export function I18nProvider({ children }: { children: React.ReactNode }) {
  const [locale, setLocaleState] = React.useState<Locale>("en");

  React.useEffect(() => {
    setLocaleState(getBrowserLocale());
  }, []);

  React.useEffect(() => {
    if (typeof window !== "undefined") {
      window.localStorage.setItem(STORAGE_KEY, locale);
      document.documentElement.lang = locale;
    }
  }, [locale]);

  const messages = dictionaries[locale];

  const value = React.useMemo<I18nContextValue>(
    () => ({
      locale,
      setLocale: setLocaleState,
      messages,
      t: (path, variables) => {
        const resolved = resolvePath(messages, path);
        const fallback = resolvePath(dictionaries.en, path);
        const template = typeof resolved === "string" ? resolved : typeof fallback === "string" ? fallback : path;

        return interpolate(template, variables);
      },
      localizeText: (text) => text[locale] ?? text.en
    }),
    [locale, messages]
  );

  return <I18nContext.Provider value={value}>{children}</I18nContext.Provider>;
}

export function useI18n() {
  const context = React.useContext(I18nContext);

  if (!context) {
    throw new Error("useI18n must be used within I18nProvider");
  }

  return context;
}
